import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API_URL } from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import HistorialLotesModal from '../components/HistorialLotesModal';
import './Dashboard.css';

const Liquidacion = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedConcesionario, setSelectedConcesionario] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('gestion'); // 'gestion' | 'cuenta_cobro'

  // Tab 1: Gestión de Cobros states
  const [tramites, setTramites] = useState([]);
  const [loadingTramites, setLoadingTramites] = useState(false);
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterDocStatus, setFilterDocStatus] = useState('todos'); // 'todos' | 'completo' | 'incompleto'
  const [search, setSearch] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [estadoTramite, setEstadoTramite] = useState('');
  const [tipoTramiteFilter, setTipoTramiteFilter] = useState('');
  const [selectedTramites, setSelectedTramites] = useState([]);
  const [zonasFilterList, setZonasFilterList] = useState([]);
  const [selectedZonaId, setSelectedZonaId] = useState('');

  // Tab 2: Cuenta de Cobro states
  const [cuentaCobroTramites, setCuentaCobroTramites] = useState([]);
  const [loadingCuentaCobro, setLoadingCuentaCobro] = useState(false);
  const [searchCuentaCobro, setSearchCuentaCobro] = useState('');
  const [selectedCuentaCobroIds, setSelectedCuentaCobroIds] = useState([]);
  const [generandoPaquete, setGenerandoPaquete] = useState(false);

  // Preview Modal state
  const [previewDoc, setPreviewDoc] = useState(null); // { title: string, url: string }
  const [showHistorialModal, setShowHistorialModal] = useState(false);

  // Tab 3: Cobro por Conceptos (Lotes, Prendas, Impuestos)
  const [conceptTab, setConceptTab] = useState('lotes'); // 'lotes' | 'prendas' | 'impuestos'
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [prendas, setPrendas] = useState([]);
  const [loadingPrendas, setLoadingPrendas] = useState(false);
  const [selectedPrendaIds, setSelectedPrendaIds] = useState([]);
  const [filterEstadoPrenda, setFilterEstadoPrenda] = useState('todos'); // 'todos' | 'pendiente' | 'cobrado' | 'recaudado'
  const [impuestos, setImpuestos] = useState([]);
  const [loadingImpuestos, setLoadingImpuestos] = useState(false);
  const [selectedImpuestoIds, setSelectedImpuestoIds] = useState([]);
  const [filterEstadoImpuesto, setFilterEstadoImpuesto] = useState('todos'); // 'todos' | 'pendiente' | 'cobrado' | 'recaudado'

  // Upload Missing Document Modal state
  const [uploadModal, setUploadModal] = useState(null); // { tramite: object }
  const [uploadDocKey, setUploadDocKey] = useState('tarjeta_propiedad');
  const [tarjetaMode, setTarjetaMode] = useState('doble_cara'); // 'doble_cara' | 'unico'
  const [singleFile, setSingleFile] = useState(null);
  const [frontalFile, setFrontalFile] = useState(null);
  const [traseraFile, setTraseraFile] = useState(null);
  const [subiendoDoc, setSubiendoDoc] = useState(false);
  const [reextractingFacturas, setReextractingFacturas] = useState(false);

  useEffect(() => {
    if (user?.role !== 'superadmin' && user?.profile_name !== 'superadmin' && user?.profile_name !== 'auxiliar transiapp') {
      navigate('/dashboard');
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/liquidacion/stats');
      setStats(res.data);
      setError('');
    } catch (err) {
      setError('Error al cargar estadísticas de liquidación');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchZonasForConcesionario = async (concId) => {
    try {
      const res = await axios.get(`/api/zonas?concesionario_id=${concId}`);
      setZonasFilterList(res.data);
    } catch (err) {
      console.error('Error al cargar zonas para filtro en liquidación:', err);
      setZonasFilterList([]);
    }
  };

  const loadTramites = async (concesionarioId) => {
    try {
      setLoadingTramites(true);
      const queryParams = new URLSearchParams({
        estado: filterEstado,
        filter_doc_status: filterDocStatus,
        search,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        estado_tramite: estadoTramite,
        tipo_tramite: tipoTramiteFilter,
        zona_id: selectedZonaId
      }).toString();
      
      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/tramites?${queryParams}`);
      setTramites(res.data);
      setSelectedTramites([]);
    } catch (err) {
      console.error(err);
      alert('Error al cargar los trámites');
    } finally {
      setLoadingTramites(false);
    }
  };

  const loadCuentaCobro = async (concesionarioId) => {
    try {
      setLoadingCuentaCobro(true);
      const queryParams = new URLSearchParams({
        filter_doc_status: filterDocStatus,
        search: searchCuentaCobro,
        zona_id: selectedZonaId,
        tipo_tramite: tipoTramiteFilter,
        estado_tramite: estadoTramite
      }).toString();

      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/cuenta-cobro?${queryParams}`);
      setCuentaCobroTramites(res.data);
      setSelectedCuentaCobroIds([]);
    } catch (err) {
      console.error(err);
      alert('Error al cargar trámites para Cuenta de Cobro');
    } finally {
      setLoadingCuentaCobro(false);
    }
  };

  const loadLotes = async (concesionarioId) => {
    try {
      setLoadingLotes(true);
      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/lotes`);
      setLotes(res.data);
    } catch (err) {
      console.error('Error al cargar lotes:', err);
    } finally {
      setLoadingLotes(false);
    }
  };

  const loadPrendas = async (concesionarioId) => {
    try {
      setLoadingPrendas(true);
      const queryParams = new URLSearchParams({
        estado_cobro_prenda: filterEstadoPrenda
      }).toString();
      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/prendas?${queryParams}`);
      setPrendas(res.data);
      setSelectedPrendaIds([]);
    } catch (err) {
      console.error('Error al cargar prendas:', err);
    } finally {
      setLoadingPrendas(false);
    }
  };

  const loadImpuestos = async (concesionarioId) => {
    try {
      setLoadingImpuestos(true);
      const queryParams = new URLSearchParams({
        estado_cobro_impuesto: filterEstadoImpuesto
      }).toString();
      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/impuestos?${queryParams}`);
      setImpuestos(res.data);
      setSelectedImpuestoIds([]);
    } catch (err) {
      console.error('Error al cargar impuestos:', err);
    } finally {
      setLoadingImpuestos(false);
    }
  };

  useEffect(() => {
    if (selectedConcesionario) {
      fetchZonasForConcesionario(selectedConcesionario.concesionario_id);
    }
  }, [selectedConcesionario]);

  useEffect(() => {
    if (selectedConcesionario) {
      if (activeSubTab === 'gestion') {
        loadTramites(selectedConcesionario.concesionario_id);
      } else if (activeSubTab === 'cuenta_cobro') {
        loadCuentaCobro(selectedConcesionario.concesionario_id);
      } else if (activeSubTab === 'cobro_conceptos') {
        loadLotes(selectedConcesionario.concesionario_id);
        loadPrendas(selectedConcesionario.concesionario_id);
        loadImpuestos(selectedConcesionario.concesionario_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEstado, selectedConcesionario, search, fechaInicio, fechaFin, estadoTramite, tipoTramiteFilter, activeSubTab, filterDocStatus, searchCuentaCobro, selectedZonaId, filterEstadoPrenda, filterEstadoImpuesto]);

  const getCombinedZonasOptions = () => {
    const map = new Map();
    zonasFilterList.forEach(z => {
      if (z.id) map.set(String(z.id), z.name);
    });
    tramites.forEach(t => {
      if (t.zona_id && t.zona_name) {
        map.set(String(t.zona_id), t.zona_name);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'es', { sensitivity: 'base' }));
  };

  const handleSelectConcesionario = (stat) => {
    setSelectedConcesionario(stat);
    setFilterEstado('todos');
    setSelectedZonaId('');
    setActiveSubTab('gestion');
    fetchZonasForConcesionario(stat.concesionario_id);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTramites(tramites.map(t => t.id));
    } else {
      setSelectedTramites([]);
    }
  };

  const handleSelectTramite = (id) => {
    if (selectedTramites.includes(id)) {
      setSelectedTramites(selectedTramites.filter(tId => tId !== id));
    } else {
      setSelectedTramites([...selectedTramites, id]);
    }
  };

  const handleSelectAllCuentaCobro = (e) => {
    if (e.target.checked) {
      setSelectedCuentaCobroIds(cuentaCobroTramites.map(t => t.id));
    } else {
      setSelectedCuentaCobroIds([]);
    }
  };

  const handleSelectTramiteCuentaCobro = (id) => {
    if (selectedCuentaCobroIds.includes(id)) {
      setSelectedCuentaCobroIds(selectedCuentaCobroIds.filter(tId => tId !== id));
    } else {
      setSelectedCuentaCobroIds([...selectedCuentaCobroIds, id]);
    }
  };

  const handleUpdateStatus = async (nuevoEstado, targetTramiteId = null, customConfirmMsg = null) => {
    const tramiteIds = targetTramiteId ? [targetTramiteId] : selectedTramites;

    if (tramiteIds.length === 0) {
      alert('Por favor selecciona al menos un trámite');
      return;
    }
    
    const confirmMsg = customConfirmMsg || `¿Estás seguro de cambiar los ${tramiteIds.length} trámites seleccionados al estado '${nuevoEstado.toUpperCase()}'?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      setLoadingTramites(true);
      const res = await axios.put('/api/liquidacion/update-status', {
        tramite_ids: tramiteIds,
        nuevo_estado: nuevoEstado
      });
      alert(res.data?.message || `Trámites actualizados a '${nuevoEstado}' exitosamente.`);

      // Si se marcaron como COBRADO, generar y descargar automáticamente el paquete de documentos soporte unificados por placa
      if (nuevoEstado === 'cobrado') {
        try {
          const packageRes = await axios.post(
            '/api/liquidacion/generar-paquete',
            { tramite_ids: tramiteIds },
            { responseType: 'blob' }
          );

          let filename = tramiteIds.length === 1 ? 'Tramite_Unificado.pdf' : 'Cuenta_de_Cobro.zip';
          const disposition = packageRes.headers['content-disposition'];
          if (disposition && disposition.includes('filename=')) {
            const match = disposition.match(/filename="?([^";]+)"?/);
            if (match && match[1]) filename = match[1];
          }

          const url = window.URL.createObjectURL(new Blob([packageRes.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (pkgErr) {
          console.error('Error al descargar paquete automático:', pkgErr);
          alert('⚠️ Trámites cobrados correctamente, pero hubo un detalle al descargar el paquete automático. Puede descargarlo desde la opción de paquete.');
        }
      }

      await fetchStats();
      loadTramites(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.message || 'Error al actualizar el estado de los trámites';
      alert(errorMsg);
      setLoadingTramites(false);
    }
  };

  const handleGenerarPaquete = async () => {
    if (selectedCuentaCobroIds.length === 0) {
      alert('Por favor selecciona al menos un trámite para generar el paquete');
      return;
    }

    try {
      setGenerandoPaquete(true);
      const res = await axios.post(
        '/api/liquidacion/generar-paquete',
        { tramite_ids: selectedCuentaCobroIds },
        { responseType: 'blob' }
      );

      let filename = selectedCuentaCobroIds.length === 1 ? 'Tramite_Unificado.pdf' : 'Cuenta_de_Cobro.zip';
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error al generar paquete de Cuenta de Cobro:', err);
      alert('❌ Error al generar el paquete de documentos unificados');
    } finally {
      setGenerandoPaquete(false);
    }
  };

  const handleCobrarLoteBase = async (loteId) => {
    if (!window.confirm(`¿Estás seguro de registrar el cobro del Lote Base #${loteId}?`)) return;
    try {
      await axios.put(`/api/liquidacion/lote/${loteId}/cobrar`);
      alert('✅ Cobro de lote base registrado exitosamente.');
      if (selectedConcesionario) loadLotes(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al cobrar lote base: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdatePrendaStatus = async (nuevoEstado) => {
    if (selectedPrendaIds.length === 0) {
      alert('⚠️ Por favor selecciona al menos un trámite con prenda');
      return;
    }
    if (!window.confirm(`¿Estás seguro de marcar el diferencial de prenda como '${nuevoEstado.toUpperCase()}' para los ${selectedPrendaIds.length} trámites seleccionados?`)) return;
    try {
      await axios.put('/api/liquidacion/update-prenda-status', {
        tramite_ids: selectedPrendaIds,
        nuevo_estado: nuevoEstado
      });
      alert('✅ Estado de prenda actualizado exitosamente.');
      if (selectedConcesionario) loadPrendas(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al actualizar estado de prenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdateImpuestoStatus = async (nuevoEstado) => {
    if (selectedImpuestoIds.length === 0) {
      alert('⚠️ Por favor selecciona al menos un trámite con impuesto');
      return;
    }
    if (!window.confirm(`¿Estás seguro de marcar el impuesto vehicular como '${nuevoEstado.toUpperCase()}' para los ${selectedImpuestoIds.length} trámites seleccionados?`)) return;
    try {
      await axios.put('/api/liquidacion/update-impuesto-status', {
        tramite_ids: selectedImpuestoIds,
        nuevo_estado: nuevoEstado
      });
      if (nuevoEstado === 'cobrado') {
        // Descargar ZIP con un PDF por placa (Liquidación + Soporte del impuesto) y Excel resumen
        try {
          const zipRes = await axios.post('/api/liquidacion/generar-paquete-impuestos',
            { tramite_ids: selectedImpuestoIds }, { responseType: 'blob' });
          const cd = zipRes.headers['content-disposition'] || '';
          const fname = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `Impuestos_${Date.now()}.zip`;
          const url = window.URL.createObjectURL(new Blob([zipRes.data], { type: 'application/zip' }));
          const a = document.createElement('a');
          a.href = url;
          a.download = fname;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (zipErr) {
          console.error(zipErr);
          alert('⚠️ El cobro quedó registrado, pero no se pudo generar el ZIP. Puede descargarlo desde el Historial.');
        }
      }
      alert('✅ Estado de impuesto vehicular actualizado exitosamente. El lote quedó registrado en el Historial.');
      if (selectedConcesionario) loadImpuestos(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al actualizar estado de impuesto: ' + (err.response?.data?.message || err.message));
    }
  };

  const exportLotesToExcel = () => {
    if (!lotes || lotes.length === 0) {
      alert('No hay lotes para exportar.');
      return;
    }

    const exportData = lotes.map(l => ({
      'Lote ID': `Lote #${l.id}`,
      'Organismo de Tránsito': l.transito_name || 'N/A',
      'Placa Inicial': l.start_plate || 'N/A',
      'Placa Final': l.end_plate || 'N/A',
      'Cantidad Solicitada': l.requested_quantity || 0,
      'Placas Consumidas': l.consumed_count || 0,
      'Fecha Entrega': formatDateShort(l.created_at),
      'Estado Cobro': (l.lote_estado_cobro || 'pendiente').toUpperCase(),
      'Fecha Cobro': l.lote_fecha_cobro ? formatDateShort(l.lote_fecha_cobro) : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lotes Base');
    const concName = selectedConcesionario?.concesionario_name ? selectedConcesionario.concesionario_name.replace(/[^a-z0-9]/gi, '_') : 'Concesionario';
    XLSX.writeFile(workbook, `Cobro_Lotes_${concName}_${formatDateShort(new Date()).replace(/\//g, '-')}.xlsx`);
  };

  const exportPrendasToExcel = () => {
    if (!prendas || prendas.length === 0) {
      alert('No hay trámites con prenda para exportar.');
      return;
    }

    const exportData = prendas.map(p => {
      const datos = p.datos_adicionales || {};
      const vendedorAsesor = formatFullName(
        datos.vendedor_factura ||
        datos.asesor ||
        datos.vendedor ||
        datos.nombre_asesor ||
        p.vendedor_asesor ||
        p.usuario_creador
      );
      const puntoVenta = p.punto_venta_name || datos.punto_venta_factura || datos.punto_venta_nombre || datos.punto_venta || selectedConcesionario?.concesionario_name || 'N/A';

      return {
        'ID Trámite': p.id,
        'Placa': p.plate || 'Sin Placa',
        'Organismo de Tránsito': p.transito_name || 'N/A',
        'Nombre Propietario': datos.propietario || 'N/A',
        'Tipo Documento': datos.tipo_documento || 'CC',
        'Cédula / Documento': datos.documento || 'N/A',
        'Punto de Venta': puntoVenta,
        'Vendedor / Asesor': vendedorAsesor,
        'Tipo de Trámite': p.tipo_tramite || 'N/A',
        'Acreedor Prendario': datos.acreedor_nombre || 'N/A',
        'Estado Cobro Prenda': (p.prenda_estado_cobro || 'pendiente').toUpperCase()
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Diferencial Prendas');
    const concName = selectedConcesionario?.concesionario_name ? selectedConcesionario.concesionario_name.replace(/[^a-z0-9]/gi, '_') : 'Concesionario';
    XLSX.writeFile(workbook, `Cobro_Prendas_${concName}_${formatDateShort(new Date()).replace(/\//g, '-')}.xlsx`);
  };

  const exportImpuestosToExcel = () => {
    if (!impuestos || impuestos.length === 0) {
      alert('No hay trámites con impuesto para exportar.');
      return;
    }

    const exportData = impuestos.map(imp => {
      const datos = imp.datos_adicionales || {};
      const vendedorAsesor = formatFullName(
        datos.vendedor_factura ||
        datos.asesor ||
        datos.vendedor ||
        datos.nombre_asesor ||
        imp.vendedor_asesor ||
        imp.usuario_creador
      );
      const puntoVenta = imp.punto_venta_name || datos.punto_venta_factura || datos.punto_venta_nombre || datos.punto_venta || selectedConcesionario?.concesionario_name || 'N/A';

      return {
        'ID Trámite': imp.id,
        'Placa': imp.plate || 'Sin Placa',
        'Organismo de Tránsito': imp.transito_name || 'N/A',
        'Nombre Propietario': datos.propietario || 'N/A',
        'Tipo Documento': datos.tipo_documento || 'CC',
        'Cédula / Documento': datos.documento || 'N/A',
        'Punto de Venta': puntoVenta,
        'Vendedor / Asesor': vendedorAsesor,
        'Valor Impuesto': parseFloat(imp.impuesto_valor || 0),
        'Estado Cobro Impuesto': (imp.impuesto_estado_cobro || 'pendiente').toUpperCase()
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reembolso Impuestos');
    const concName = selectedConcesionario?.concesionario_name ? selectedConcesionario.concesionario_name.replace(/[^a-z0-9]/gi, '_') : 'Concesionario';
    XLSX.writeFile(workbook, `Cobro_Impuestos_${concName}_${formatDateShort(new Date()).replace(/\//g, '-')}.xlsx`);
  };

  const handleOpenPreview = (docLabel, docUrl, plate) => {
    if (!docUrl) {
      alert(`⚠️ El documento '${docLabel}' aún no ha sido cargado.`);
      return;
    }
    const fullUrl = docUrl.startsWith('http') ? docUrl : `${API_URL}${docUrl}`;
    setPreviewDoc({
      title: `${docLabel} (${plate || 'Sin Placa'})`,
      url: fullUrl
    });
  };

  // Helper para obtener SOLO los documentos que hacen falta para este trámite
  const getAvailableDocsForUpload = (t) => {
    if (!t || !t.checklist) return [];
    
    // Filtrar estrictamente los que faltan y son requeridos (no N/A)
    const missing = Object.entries(t.checklist)
      .filter(([key, doc]) => doc.required && !doc.present && !doc.is_na)
      .map(([key, doc]) => ({ key, label: doc.label }));

    if (missing.length > 0) {
      return missing;
    }

    // Si ya no falta ninguno, se muestran los requeridos para permitir actualizar/reemplazar si se desea
    return Object.entries(t.checklist)
      .filter(([key, doc]) => doc.required && !doc.is_na)
      .map(([key, doc]) => ({ key, label: `${doc.label} (Actualizar)` }));
  };

  const handleOpenUploadModal = (tramite, targetDocKey = null) => {
    setUploadModal(tramite);
    const available = getAvailableDocsForUpload(tramite);
    
    let initialKey = targetDocKey;
    if (!initialKey || !available.some(d => d.key === initialKey)) {
      initialKey = available[0]?.key || 'tarjeta_propiedad';
    }

    setUploadDocKey(initialKey);
    setTarjetaMode('doble_cara');
    setSingleFile(null);
    setFrontalFile(null);
    setTraseraFile(null);
  };

  const handleSubirDocumentoSubmit = async (e) => {
    e.preventDefault();
    if (!uploadModal) return;

    const data = new FormData();
    data.append('tramite_id', uploadModal.id);
    data.append('tipo_documento_key', uploadDocKey);
    data.append('tipo_documento', uploadDocKey);

    if (uploadDocKey === 'tarjeta_propiedad' && tarjetaMode === 'doble_cara') {
      if (!frontalFile || !traseraFile) {
        alert('⚠️ Debe seleccionar ambas caras (Frontal y Trasera) para la Tarjeta de Propiedad');
        return;
      }
      data.append('tarjeta_frontal', frontalFile);
      data.append('tarjeta_trasera', traseraFile);
    } else {
      if (!singleFile) {
        alert('⚠️ Debe seleccionar el archivo del documento a cargar');
        return;
      }
      data.append('archivo', singleFile);
    }

    try {
      setSubiendoDoc(true);
      await axios.post('/api/liquidacion/subir-documento-faltante', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('✅ Documento cargado exitosamente.');
      setUploadModal(null);
      loadCuentaCobro(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al subir documento: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubiendoDoc(false);
    }
  };

  // Helper para formato corto de fechas (DD/MM/YYYY)
  const formatDateShort = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper para formatear nombre completo
  const formatFullName = (username) => {
    if (!username) return 'N/A';
    return username
      .replace(/[._]/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper para extraer el código limpio de Factura Electrónica
  const cleanFacturaCode = (str) => {
    if (!str || str === 'N/A') return 'N/A';
    const s = String(str).trim();

    // 0. Descartar si dice "NO", "SIN FACTURA", etc.
    if (/^(NO|N\/A|SIN FACTURA|SIN_FACTURA|NINGUNA|NINGUNO|FALSE|UNDEFINED|NULL)$/i.test(s)) {
      return 'N/A';
    }

    // 0b. Descartar inmediatamente si es una ruta de archivo
    if (
      s.toLowerCase().endsWith('.pdf') ||
      s.toLowerCase().endsWith('.png') ||
      s.toLowerCase().endsWith('.jpg') ||
      /^uploads[\\/]/i.test(s) ||
      /[\\/]uploads[\\/]/i.test(s) ||
      s.toLowerCase().startsWith('reporte ')
    ) {
      return 'N/A';
    }

    // 1. Descartar si el valor completo es solo un año, prefijo inválido o NIT
    if (/^(202[0-9]|DE202[0-9]|HORAS202[0-9]|NIT\d*)$/i.test(s)) {
      return 'N/A';
    }

    // Lista de años a ignorar cuando aparecen solos
    const FORBIDDEN_YEARS = new Set(['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027']);

    // 2. PRIMERO: buscar número precedido por palabra clave (más preciso)
    const matchNum = s.match(/\b(?:FACTURA|FACT|FE|BILL|N[°ºo]?\.?\s*|NUM\.?\s*|NUMERO\s*|NRO\.?\s*)(\d{3,12})\b/i);
    if (matchNum) {
      const num = matchNum[1];
      if (!FORBIDDEN_YEARS.has(num)) return num;
    }

    // 3. SEGUNDO: buscar código alfanumérico tipo "FEDE00007898", "FE-1234", "TMSM141"
    //    Patrón: 2-6 letras seguidas (sin espacio) de 2-12 dígitos
    const matchPrefix = s.match(/\b([A-Z]{2,6}-?\d{2,12})\b/i);
    if (matchPrefix) {
      const code = matchPrefix[1].replace(/[-\s]+/g, '').toUpperCase();
      const FORBIDDEN_CODES = new Set(['FACTURA', 'REPORTE', 'CUPL', 'SOAT', 'DOCUMENTO', 'ESTADO', 'PLACA', 'PDF', 'GUAMAL', 'NIT', 'NIT901', 'DE2026', 'HORAS2026', 'HORAS2025']);
      const endsWithYear = /202[0-9]$/.test(code);
      if (!FORBIDDEN_CODES.has(code) && !endsWithYear) {
        return code;
      }
    }

    // 4. TERCERO: cualquier número de 4-12 dígitos que no sea un año
    const matchAnyNum = s.match(/\b(\d{4,12})\b/g);
    if (matchAnyNum) {
      for (const num of matchAnyNum) {
        if (!FORBIDDEN_YEARS.has(num) && !/^\d{13,}$/.test(num)) {
          // Excluir también timestamps de 13+ dígitos (epoch ms)
          return num;
        }
      }
    }

    // 5. Si el string corto no es sospechoso, devolverlo tal cual
    if (s.length <= 20 && !/\s/.test(s) && !s.toLowerCase().startsWith('factura')) {
      return s;
    }

    return 'N/A';
  };

  const getTramiteDetails = (t) => {
    const datos = t.datos_adicionales || {};
    const v = datos.vehicleDetails?.vehiculo || {};

    const propietario = datos.propietario || datos.nombre_propietario || t.propietario || 'N/A';
    const documento = datos.documento || datos.identificacion || t.documento_propietario || 'N/A';
    const marca = datos.marca || v.marca || 'N/A';
    const linea = datos.linea || v.linea || 'N/A';
    const cc = datos.cilindraje || v.cilindraje || v.datos_completos?.rna?.datos?.cilindraje;
    const cilindraje = cc ? `${cc} cc` : 'N/A';
    const rawFactura = t.factura_numero || datos.factura_electronica || datos.numero_factura || datos.factura_numero || datos.num_factura || datos.factura || 'N/A';
    const factura = cleanFacturaCode(rawFactura);

    const rawVendedor = datos.vendedor_factura ||
      datos.asesor ||
      datos.vendedor ||
      datos.nombre_asesor ||
      datos.creador ||
      datos.usuario_creador ||
      t.vendedor_asesor ||
      t.usuario_creador;

    const vendedorAsesor = formatFullName(rawVendedor);
    const usuarioCreador = formatFullName(t.usuario_creador || rawVendedor);

    const puntoVenta = t.punto_venta_name ||
      datos.punto_venta_factura ||
      datos.punto_venta_nombre ||
      datos.punto_venta ||
      datos.punto_de_venta ||
      datos.sede ||
      datos.sucursal ||
      datos.tienda ||
      selectedConcesionario?.concesionario_name ||
      'N/A';

    // Extraer valores numéricos para exportación
    // Valor Liquidación Tránsito: Únicamente si la liquidación fue cargada/extraída en el trámite. Si no está cargada, es 0.
    const rawLiq = datos.total_liquidacion || datos.valor_liquidacion || datos.liquidacion_total || datos.total_liq || datos.valor_liq_transito || 0;
    const valorLiquidacionTransito = parseFloat(rawLiq) || 0;

    // Valor CUPL: Monto del CUPL o tarifa base del tipo de trámite
    const rawCupl = datos.cupl_total || datos.valor_cupl || datos.cupl_valor || datos.cupl_val || datos.monto_cupl || t.valor_tramite || 0;
    const valorCupl = parseFloat(rawCupl) || 0;

    const rawImp = t.impuesto_valor || datos.impuesto_valor || datos.valor_impuesto || datos.impuesto_monto || 0;
    const valorImpuesto = parseFloat(rawImp) || 0;

    const rawSoat = datos.soat_info?.valor_soat || datos.valor_soat || datos.soat_valor || datos.monto_soat || (t.soat_monto ? parseFloat(t.soat_monto) : 0);
    const valorSoat = parseFloat(String(rawSoat).replace(/[^0-9.]/g, '')) || 0;

    return { 
      propietario, 
      documento, 
      marca, 
      linea, 
      cilindraje, 
      factura, 
      vendedorAsesor,
      usuarioCreador, 
      puntoVenta,
      valorLiquidacionTransito,
      valorCupl,
      valorImpuesto,
      valorSoat
    };
  };

  const handleReextractFacturas = async () => {
    if (!window.confirm('¿Deseas re-extraer el número de factura de todos los trámites que tienen N/A? Esto puede tomar varios minutos.')) return;
    try {
      setReextractingFacturas(true);
      const res = await axios.post('/api/liquidacion/reextract-facturas');
      const { total, fixed, failed } = res.data;
      alert(`✅ Re-extracción completa.\n\nTotal procesados: ${total}\nCorregidos: ${fixed}\nNo detectados/Error: ${failed}`);
      if (selectedConcesionario) loadTramites(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al re-extraer facturas: ' + (err.response?.data?.message || err.message));
    } finally {
      setReextractingFacturas(false);
    }
  };

  const exportToExcel = () => {
    if (tramites.length === 0) {
      alert('No hay datos para exportar con los filtros actuales.');
      return;
    }

    const hasAlreadyCharged = tramites.some(t => {
      const st = (t.liquidacion_estado || '').toLowerCase();
      return st === 'cobrado' || st === 'recaudado' || st === 'pagado';
    });

    if (hasAlreadyCharged && filterEstado === 'todos') {
      if (!window.confirm('⚠️ ADVERTENCIA ⚠️\n\nEstás exportando TODOS los trámites, incluyendo los que YA ESTÁN COBRADOS o RECAUDADOS.\n\nSi envías este archivo de Excel para cobrar al concesionario, estarás cobrando trámites REPETIDOS que ya pagaron.\n\nTe recomendamos cancelar, seleccionar el filtro amarillo "1. Pendientes" y volver a exportar.\n\n¿Estás SEGURO de que deseas exportar todos de todos modos?')) {
        return;
      }
    }

    const isSuperAdmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';

    const exportData = tramites.map(t => {
      const details = getTramiteDetails(t);
      const row = {
        'ID Trámite': t.id,
        'Fecha Creación': formatDateShort(t.created_at),
        'Fecha Ejecución': t.fecha_ejecucion ? formatDateShort(t.fecha_ejecucion) : 'N/A',
        'Fecha Cobro': t.liquidacion_fecha_cobro ? formatDateShort(t.liquidacion_fecha_cobro) : 'N/A',
        'Fecha Pago': t.liquidacion_fecha_pago ? formatDateShort(t.liquidacion_fecha_pago) : 'N/A',
        'Punto de Venta': details.puntoVenta,
        'Zona': t.zona_name || 'N/A',
        'Placa': t.plate || 'Sin Placa',
        'Nombre Propietario': details.propietario,
        'Cédula Propietario': details.documento,
        'Marca': details.marca,
        'Línea': details.linea,
        'Cilindraje': details.cilindraje,
        'Factura Electrónica': details.factura,
        'Tipo de Trámite': t.tipo_tramite,
        'Organismo de Tránsito': t.transito_name || 'N/A',
        'Vendedor / Asesor': details.vendedorAsesor,
        'Usuario Creador': details.usuarioCreador,
        'Estado del Trámite': t.estado_tramite,
        'Estado Liquidación': t.liquidacion_estado.toUpperCase()
      };

      row['Valor Liquidación Tránsito'] = details.valorLiquidacionTransito;
      row['Valor CUPL'] = details.valorCupl;
      row['Valor Impuesto'] = details.valorImpuesto;
      row['Valor SOAT'] = details.valorSoat;

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Liquidación');
    XLSX.writeFile(workbook, `Liquidacion_${selectedConcesionario?.concesionario_name.replace(/[^a-z0-9]/gi, '_')}_${formatDateShort(new Date()).replace(/\//g, '-')}.xlsx`);
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="💵 Liquidación de Trámites" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          
          {!selectedConcesionario ? (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Resumen por Concesionario</h2>
                  <p className="subtitle">Selecciona un concesionario para ver sus trámites y gestionar cobros</p>
                </div>
                <button onClick={fetchStats} className="btn-secondary">
                  🔄 Actualizar
                </button>
              </div>

              {loading ? (
                <p className="loading">Cargando concesionarios...</p>
              ) : (
                <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {stats.map((stat) => (
                    <div 
                      key={stat.concesionario_id} 
                      className="stat-card" 
                      style={{ cursor: 'pointer', borderLeft: '4px solid #3b82f6' }}
                      onClick={() => handleSelectConcesionario(stat)}
                    >
                      <div className="stat-details" style={{ width: '100%' }}>
                        <span className="stat-label" style={{ fontSize: '1.1rem', color: '#1e293b', marginBottom: '10px' }}>
                          🏢 {stat.concesionario_name}
                        </span>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', marginTop: '10px', fontSize: '11px', textAlign: 'center' }}>
                          <div>
                            <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '15px' }}>{stat.pendientes}</div>
                            <div style={{ color: '#64748b' }}>Pendientes</div>
                          </div>
                          <div>
                            <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '15px' }}>{stat.cobrados}</div>
                            <div style={{ color: '#64748b' }}>Cobrados</div>
                          </div>
                          <div>
                            <div style={{ color: '#059669', fontWeight: 'bold', fontSize: '15px' }}>{stat.recaudados}</div>
                            <div style={{ color: '#64748b' }}>Recaudados</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {stats.length === 0 && (
                    <div className="no-data" style={{ gridColumn: '1 / -1' }}>No hay datos de liquidación disponibles.</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Concesionario: {selectedConcesionario.concesionario_name}</h2>
                  <p className="subtitle">Gestión de liquidación de cobros de trámites</p>
                </div>
                <button onClick={() => setSelectedConcesionario(null)} className="btn-secondary">
                  ⬅️ Volver a Resumen
                </button>
              </div>

              {/* BARRA DE PESTAÑAS DE LIQUIDACIÓN */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: '2px solid #cbd5e1', paddingBottom: '12px' }}>
                <button 
                  onClick={() => setActiveSubTab('gestion')}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px',
                    background: activeSubTab === 'gestion' ? '#3b82f6' : '#f1f5f9',
                    color: activeSubTab === 'gestion' ? '#ffffff' : '#475569',
                    boxShadow: activeSubTab === 'gestion' ? '0 2px 4px rgba(59,130,246,0.3)' : 'none'
                  }}
                >
                  📊 Liquidación General
                </button>
                <button 
                  onClick={() => setActiveSubTab('cuenta_cobro')}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px',
                    background: activeSubTab === 'cuenta_cobro' ? '#3b82f6' : '#f1f5f9',
                    color: activeSubTab === 'cuenta_cobro' ? '#ffffff' : '#475569',
                    boxShadow: activeSubTab === 'cuenta_cobro' ? '0 2px 4px rgba(59,130,246,0.3)' : 'none'
                  }}
                >
                  🧾 Cuenta de Cobro / Paquete
                </button>
                <button 
                  onClick={() => setActiveSubTab('cobro_conceptos')}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px',
                    background: activeSubTab === 'cobro_conceptos' ? '#8b5cf6' : '#f1f5f9',
                    color: activeSubTab === 'cobro_conceptos' ? '#ffffff' : '#475569',
                    boxShadow: activeSubTab === 'cobro_conceptos' ? '0 2px 4px rgba(139,92,246,0.3)' : 'none'
                  }}
                >
                  🏷️ Cobro por Conceptos (Lotes / Prendas / Impuestos)
                </button>
                <button 
                  onClick={() => setShowHistorialModal(true)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px',
                    background: '#0284c7',
                    color: '#ffffff',
                    boxShadow: '0 2px 4px rgba(2,132,199,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginLeft: 'auto'
                  }}
                >
                  📜 Historial Lotes Cobrados / Recaudados
                </button>
              </div>

              {activeSubTab === 'gestion' && (
                <>
                  <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="quick-filters" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button 
                        className={`btn-filter ${filterEstado === 'todos' ? 'active' : ''}`}
                        onClick={() => setFilterEstado('todos')}
                      >
                        Todos los Cobros
                      </button>
                      <button 
                        className={`btn-filter ${filterEstado === 'pendiente' ? 'active' : ''}`}
                        onClick={() => setFilterEstado('pendiente')}
                        style={filterEstado === 'pendiente' ? { background: '#fef3c7', color: '#b45309', border: '1px solid #f59e0b' } : {}}
                      >
                        1. Pendientes
                      </button>
                      <button 
                        className={`btn-filter ${filterEstado === 'cobrado' ? 'active' : ''}`}
                        onClick={() => setFilterEstado('cobrado')}
                        style={filterEstado === 'cobrado' ? { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #3b82f6' } : {}}
                      >
                        2. Cobrados
                      </button>
                      <button 
                        className={`btn-filter ${filterEstado === 'recaudado' ? 'active' : ''}`}
                        onClick={() => setFilterEstado('recaudado')}
                        style={filterEstado === 'recaudado' ? { background: '#dcfce7', color: '#14532d', border: '1px solid #22c55e' } : {}}
                      >
                        3. Recaudados
                      </button>

                      <div style={{ height: '24px', width: '1px', background: '#cbd5e1', margin: '0 4px' }} />

                      <button 
                        className={`btn-filter ${filterDocStatus === 'completo' ? 'active' : ''}`}
                        onClick={() => setFilterDocStatus(filterDocStatus === 'completo' ? 'todos' : 'completo')}
                        style={filterDocStatus === 'completo' ? { background: '#ecfdf5', color: '#047857', border: '1px solid #10b981', fontWeight: 'bold' } : { border: '1px solid #a7f3d0', color: '#047857' }}
                      >
                        ✅ Doc. Completa para Cobrar
                      </button>
                      <button 
                        className={`btn-filter ${filterDocStatus === 'incompleto' ? 'active' : ''}`}
                        onClick={() => setFilterDocStatus(filterDocStatus === 'incompleto' ? 'todos' : 'incompleto')}
                        style={filterDocStatus === 'incompleto' ? { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontWeight: 'bold' } : { border: '1px solid #fecaca', color: '#991b1b' }}
                      >
                        ⚠️ Doc. Incompleta
                      </button>
                    </div>

                    <div className="dashboard-filters" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px', padding: '20px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Buscar</label>
                        <input 
                          type="text" 
                          placeholder="🔍 Placa, ID, Propietario, Cédula..." 
                          className="form-input" 
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      
                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Estado del Trámite</label>
                        <select 
                          className="form-input" 
                          value={estadoTramite}
                          onChange={(e) => setEstadoTramite(e.target.value)}
                        >
                          <option value="">Todos los Estados</option>
                          <option value="radicado">Radicado</option>
                          <option value="pre_asignado">Pre asignado</option>
                          <option value="tramite_ejecutado">Trámite Ejecutado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      </div>

                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Zona</label>
                        <select 
                          className="form-input" 
                          value={selectedZonaId}
                          onChange={(e) => setSelectedZonaId(e.target.value)}
                        >
                          <option value="">Todas las Zonas</option>
                          {getCombinedZonasOptions().map(([id, name]) => (
                            <option key={id} value={id}>🗺️ {name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Tipo de Trámite</label>
                        <select 
                          className="form-input" 
                          value={tipoTramiteFilter}
                          onChange={(e) => setTipoTramiteFilter(e.target.value)}
                        >
                          <option value="">Todos los Tipos</option>
                          <option value="matricula inicial">Matrícula Inicial</option>
                          <option value="matricula inicial con prenda">Matrícula Inicial con Prenda</option>
                        </select>
                      </div>

                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '150px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Desde</label>
                        <input 
                          type="date" 
                          className="form-input" 
                          value={fechaInicio}
                          onChange={(e) => setFechaInicio(e.target.value)}
                        />
                      </div>
                      
                      <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '150px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Hasta</label>
                        <input 
                          type="date" 
                          className="form-input" 
                          value={fechaFin}
                          onChange={(e) => setFechaFin(e.target.value)}
                        />
                      </div>
                      
                      <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                        <button className="btn-secondary" onClick={() => {
                          setSearch(''); setFechaInicio(''); setFechaFin(''); setEstadoTramite(''); setTipoTramiteFilter(''); setSelectedZonaId('');
                        }}>
                          Limpiar
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div className="bulk-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button 
                        className="btn-secondary" 
                        onClick={exportToExcel}
                        style={{ background: '#10b981', color: 'white', borderColor: '#059669', marginRight: '10px' }}
                      >
                        📥 Exportar Excel
                      </button>

                      {(user?.role === 'superadmin' || user?.profile_name === 'superadmin') && (
                        <button
                          className="btn-secondary"
                          onClick={handleReextractFacturas}
                          disabled={reextractingFacturas}
                          title="Re-extrae el número de factura de los PDFs ya subidos que aún muestran N/A"
                          style={{ background: reextractingFacturas ? '#94a3b8' : '#8b5cf6', color: 'white', borderColor: '#7c3aed' }}
                        >
                          {reextractingFacturas ? '⏳ Procesando...' : '🔍 Re-extraer Facturas N/A'}
                        </button>
                      )}

                      <span style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#64748b', marginRight: '10px' }}>
                        {selectedTramites.length} seleccionados
                      </span>
                      
                      <button 
                        className="btn-primary" 
                        onClick={() => handleUpdateStatus('pendiente')}
                        disabled={selectedTramites.length === 0}
                        style={{ background: '#f59e0b', borderColor: '#d97706' }}
                        title="Devolver a estado pendiente"
                      >
                        ⬅️ Devolver a Pendiente
                      </button>
                      
                      <button 
                        className="btn-primary" 
                        onClick={() => handleUpdateStatus('cobrado')}
                        disabled={selectedTramites.length === 0}
                        style={{ background: '#3b82f6' }}
                      >
                        🧾 1. Marcar Cobrado
                      </button>
                      
                      <button 
                        className="btn-primary" 
                        onClick={() => handleUpdateStatus('recaudado')}
                        disabled={selectedTramites.length === 0}
                        style={{ background: '#059669' }}
                      >
                        💰 2. Marcar Recaudar
                      </button>
                    </div>
                  </div>

                  {loadingTramites ? (
                    <p className="loading">Cargando trámites...</p>
                  ) : (
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px', textAlign: 'center' }}>
                                <input 
                                  type="checkbox" 
                                  onChange={handleSelectAll}
                                  checked={tramites.length > 0 && selectedTramites.length === tramites.length}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                              </th>
                              <th>ID / Placa</th>
                              <th>Propietario</th>
                              <th>Moto</th>
                              <th>Factura Electrónica</th>
                              <th>Trámite / Tránsito</th>
                              <th>Punto de Venta</th>
                              <th>Estado Trámite / Liquidación</th>
                              <th>Fechas / PDFs</th>
                              <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tramites.map(t => {
                              const details = getTramiteDetails(t);
                              const st = (t.liquidacion_estado || 'pendiente').toLowerCase();

                              return (
                                <tr key={t.id} className={selectedTramites.includes(t.id) ? 'selected-row' : ''}>
                                  <td style={{ textAlign: 'center' }}>
                                    <input 
                                      type="checkbox"
                                      checked={selectedTramites.includes(t.id)}
                                      onChange={() => handleSelectTramite(t.id)}
                                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                  </td>
                                  <td>
                                    <strong>{t.plate || 'Sin Placa'}</strong>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{t.id}</div>
                                  </td>
                                  <td>
                                    <strong>{details.propietario}</strong>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>CC: {details.documento}</div>
                                  </td>
                                  <td>
                                    <div><strong>{details.marca}</strong> {details.linea !== 'N/A' ? `- ${details.linea}` : ''}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>Cilindraje: {details.cilindraje}</div>
                                  </td>
                                  <td>
                                    {details.factura !== 'N/A' ? (
                                      t.factura_ruta ? (
                                        <a
                                          href={`${API_URL}/uploads/tramites/${t.factura_ruta.split(/[\\/]/).pop()}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ textDecoration: 'none' }}
                                          title="Ver Factura PDF"
                                        >
                                          <span className="plate-badge-sm" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: '700', fontSize: '13px' }}>
                                            🧾 {details.factura}
                                          </span>
                                        </a>
                                      ) : (
                                        <span className="plate-badge-sm" style={{ background: '#f8fafc', color: '#1e293b', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '13px' }}>
                                          🧾 {details.factura}
                                        </span>
                                      )
                                    ) : (
                                      <span style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>N/A</span>
                                    )}
                                  </td>
                                  <td>
                                    <div>{t.tipo_tramite}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{t.transito_name || 'N/A'}</div>
                                  </td>
                                  <td>
                                    <span className="plate-badge-sm" style={{ background: '#f8fafc', color: '#1e293b', border: '1px solid #e2e8f0', fontWeight: '600' }}>
                                      🏢 {details.puntoVenta}
                                    </span>
                                    {details.vendedorAsesor && details.vendedorAsesor !== 'N/A' && (
                                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontWeight: '600' }}>
                                        👤 {details.vendedorAsesor}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div style={{ marginBottom: '4px' }}>
                                      <span className="plate-badge-sm" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                                        {t.estado_tramite}
                                      </span>
                                    </div>
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      backgroundColor: 
                                        st === 'recaudado' ? '#dcfce7' :
                                        st === 'cobrado' ? '#dbeafe' :
                                        st === 'pagado' ? '#d1fae5' : 
                                        st === 'registrado' ? '#f3e8ff' : '#fef3c7',
                                      color: 
                                        st === 'recaudado' ? '#14532d' :
                                        st === 'cobrado' ? '#1e40af' :
                                        st === 'pagado' ? '#065f46' : 
                                        st === 'registrado' ? '#6b21a8' : '#92400e'
                                    }}>
                                      {st.toUpperCase()}
                                    </span>
                                  </td>
                                  <td style={{ fontSize: '12px' }}>
                                    <div><strong style={{ color: '#64748b' }}>Creado:</strong> {formatDateShort(t.created_at)}</div>
                                    <div><strong style={{ color: '#8b5cf6' }}>Ejecutado:</strong> {t.fecha_ejecucion ? formatDateShort(t.fecha_ejecucion) : 'N/A'}</div>
                                    <div><strong style={{ color: '#3b82f6' }}>Cobrado:</strong> {t.liquidacion_fecha_cobro ? formatDateShort(t.liquidacion_fecha_cobro) : 'N/A'}</div>
                                    <div><strong style={{ color: '#10b981' }}>Pagado:</strong> {t.liquidacion_fecha_pago ? formatDateShort(t.liquidacion_fecha_pago) : 'N/A'}</div>
                                    {/* 3. Pagado: En este estado se evidencian los 2 PDF */}
                                    {(st === 'pagado' || st === 'cobrado' || st === 'recaudado') && (
                                      <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                        {t.liquidacion_archivo_url && (
                                          <a href={`${API_URL}/${t.liquidacion_archivo_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', padding: '2px 6px', background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', textDecoration: 'none', fontWeight: '700' }}>
                                            📄 Liq PDF
                                          </a>
                                        )}
                                        {t.factura_ruta && (
                                          <a href={`${API_URL}/uploads/tramites/${t.factura_ruta.split(/[\\/]/).pop()}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', textDecoration: 'none', fontWeight: '700' }}>
                                            🧾 Factura PDF
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                      {/* ESTADO 1: PENDIENTE -> Botón COBRAR */}
                                      {(st === 'pendiente' || st === 'registrado') && (
                                        <button 
                                          className="btn-primary"
                                          onClick={() => handleUpdateStatus('cobrado', t.id, `¿Marcar como COBRADO el trámite #${t.id}?`)}
                                          style={{ padding: '4px 10px', fontSize: '11px', background: '#3b82f6', fontWeight: '800' }}
                                        >
                                          🧾 Cobrar
                                        </button>
                                      )}

                                      {/* ESTADO PAGADO */}
                                      {st === 'pagado' && (
                                        <>
                                          <button 
                                            className="btn-primary"
                                            onClick={() => handleUpdateStatus('cobrado', t.id, `¿Marcar como COBRADO el trámite #${t.id}?`)}
                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#3b82f6', fontWeight: '800' }}
                                          >
                                            🧾 Cobrar
                                          </button>
                                          <button 
                                            className="btn-primary"
                                            onClick={() => handleUpdateStatus('recaudado', t.id, `¿RECAUDAR el trámite #${t.id}?`)}
                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#059669', fontWeight: '800' }}
                                          >
                                            💰 Recaudar
                                          </button>
                                        </>
                                      )}

                                      {/* ESTADO 2: COBRADO -> Botón RECAUDAR y ELIMINAR (Devolver a Pendiente) */}
                                      {st === 'cobrado' && (
                                        <>
                                          <button 
                                            className="btn-primary"
                                            onClick={() => handleUpdateStatus('recaudado', t.id, `¿RECAUDAR cobro del trámite #${t.id}?`)}
                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#059669', fontWeight: '800' }}
                                          >
                                            💰 Recaudar
                                          </button>
                                          <button 
                                            className="btn-secondary"
                                            onClick={() => handleUpdateStatus('pendiente', t.id, `⚠️ ¿ESTÁS SEGURO?\n\nAl devolver el trámite #${t.id} al estado PENDIENTE, volverá a aparecer en la lista de pendientes y SE VOLVERÁ A COBRAR al concesionario.`)}
                                            style={{ padding: '4px 8px', fontSize: '11px', background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', fontWeight: '700' }}
                                            title="Devolver al estado Pendiente"
                                          >
                                            🔙 Devolver a Pendiente
                                          </button>
                                        </>
                                      )}

                                      {/* ESTADO 3: RECAUDADO -> Botón ELIMINAR (Devolver a Cobrado) */}
                                      {st === 'recaudado' && (
                                        <button 
                                          className="btn-secondary"
                                          onClick={() => handleUpdateStatus('cobrado', t.id, `⚠️ ¿ESTÁS SEGURO?\n\nAl devolver el trámite #${t.id} al estado COBRADO, se deshace el recaudo y quedará como pendiente de recaudo.`)}
                                          style={{ padding: '4px 8px', fontSize: '11px', background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', fontWeight: '700' }}
                                          title="Devolver al estado Cobrado"
                                        >
                                          🔙 Devolver a Cobrado
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            
                            {tramites.length === 0 && (
                              <tr>
                                <td colSpan="9" className="no-data" style={{ textAlign: 'center', padding: '20px' }}>
                                  No hay trámites en este estado.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* VISTA 2: CUENTA DE COBRO / PAQUETE */}
              {activeSubTab === 'cuenta_cobro' && (
                <>
                  <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="filter-group" style={{ flex: '1 1 300px' }}>
                      <input 
                        type="text" 
                        placeholder="🔍 Buscar en Cuentas de Cobro..." 
                        className="form-input" 
                        value={searchCuentaCobro}
                        onChange={(e) => setSearchCuentaCobro(e.target.value)}
                      />
                    </div>
                    <button 
                      className="btn-primary" 
                      onClick={handleGenerarPaquete}
                      disabled={selectedCuentaCobroIds.length === 0 || generandoPaquete}
                      style={{ background: '#8b5cf6' }}
                    >
                      {generandoPaquete ? '⏳ Generando Paquete...' : `📦 Generar Paquete PDF (${selectedCuentaCobroIds.length})`}
                    </button>
                  </div>

                  {loadingCuentaCobro ? (
                    <p className="loading">Cargando trámites para Cuenta de Cobro...</p>
                  ) : (
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px', textAlign: 'center' }}>
                                <input 
                                  type="checkbox" 
                                  onChange={handleSelectAllCuentaCobro}
                                  checked={cuentaCobroTramites.length > 0 && selectedCuentaCobroIds.length === cuentaCobroTramites.length}
                                />
                              </th>
                              <th>ID / Placa</th>
                              <th>Propietario</th>
                              <th>Punto de Venta</th>
                              <th>Tipo Trámite / Tránsito</th>
                              <th>Factura Electrónica</th>
                              <th>Documentos Faltantes</th>
                              <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cuentaCobroTramites.map(t => {
                              const details = getTramiteDetails(t);
                              const isComplete = t.doc_status === 'completo';
                              return (
                                <tr key={t.id} className={selectedCuentaCobroIds.includes(t.id) ? 'selected-row' : ''}>
                                  <td style={{ textAlign: 'center' }}>
                                    <input 
                                      type="checkbox"
                                      checked={selectedCuentaCobroIds.includes(t.id)}
                                      onChange={() => handleSelectTramiteCuentaCobro(t.id)}
                                    />
                                  </td>
                                  <td>
                                    <strong>{t.plate || 'Sin Placa'}</strong>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{t.id}</div>
                                  </td>
                                  <td>
                                    <strong>{details.propietario}</strong>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>CC: {details.documento}</div>
                                  </td>
                                  <td>{details.puntoVenta}</td>
                                  <td>
                                    <div>{t.tipo_tramite}</div>
                                    <small style={{ color: '#64748b' }}>{t.transito_name}</small>
                                  </td>
                                  <td>
                                    <span className={`status-badge ${details.factura !== 'N/A' ? 'status-active' : 'status-inactive'}`}>
                                      {details.factura}
                                    </span>
                                  </td>
                                  <td>
                                    {isComplete ? (
                                      <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '12px' }}>
                                        ✅ Documentación Completa
                                      </span>
                                    ) : (
                                      <button
                                        className="btn-secondary"
                                        onClick={() => handleOpenUploadModal(t)}
                                        style={{ padding: '4px 8px', fontSize: '11px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 'bold' }}
                                      >
                                        ⚠️ Cargar Faltantes
                                      </button>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      className="btn-secondary"
                                      onClick={() => handleOpenPreview('Trámite', t.liquidacion_archivo_url, t.plate)}
                                      style={{ padding: '4px 8px', fontSize: '11px' }}
                                    >
                                      👁️ Ver PDF
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {cuentaCobroTramites.length === 0 && (
                              <tr>
                                <td colSpan="8" className="no-data" style={{ textAlign: 'center', padding: '20px' }}>
                                  No hay trámites pendientes de Cuenta de Cobro.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* VISTA 3: COBRO POR CONCEPTOS (LOTES BASE, PRENDAS E IMPUESTOS) */}
              {activeSubTab === 'cobro_conceptos' && (
                <div className="conceptos-container" style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: '#f8fafc', padding: '6px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <button
                      onClick={() => setConceptTab('lotes')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '13px',
                        background: conceptTab === 'lotes' ? '#3b82f6' : 'transparent',
                        color: conceptTab === 'lotes' ? '#fff' : '#64748b'
                      }}
                    >
                      📦 1. Lotes Base Pre-pagados ({lotes.length})
                    </button>
                    <button
                      onClick={() => setConceptTab('prendas')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '13px',
                        background: conceptTab === 'prendas' ? '#8b5cf6' : 'transparent',
                        color: conceptTab === 'prendas' ? '#fff' : '#64748b'
                      }}
                    >
                      🔒 2. Diferencial por Inscripción de Prenda ({prendas.length})
                    </button>
                    <button
                      onClick={() => setConceptTab('impuestos')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '13px',
                        background: conceptTab === 'impuestos' ? '#059669' : 'transparent',
                        color: conceptTab === 'impuestos' ? '#fff' : '#64748b'
                      }}
                    >
                      📄 3. Reembolso de Impuestos Vehiculares ({impuestos.length})
                    </button>
                  </div>

                  {/* Sub-vista 1: Lotes Base */}
                  {conceptTab === 'lotes' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, color: '#1e293b' }}>📦 Lotes de Placas Asignadas (Cobro Base al Contado)</h4>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button className="btn-secondary" onClick={() => loadLotes(selectedConcesionario.concesionario_id)}>🔄 Actualizar Lotes</button>
                          <button
                            className="btn-secondary"
                            onClick={exportLotesToExcel}
                            style={{ background: '#10b981', color: 'white', borderColor: '#059669' }}
                          >
                            📥 Exportar Excel Lotes
                          </button>
                        </div>
                      </div>
                      {loadingLotes ? (
                        <p className="loading">Cargando lotes...</p>
                      ) : lotes.length === 0 ? (
                        <div className="no-data">No se encontraron lotes de placas asignados para este concesionario.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="users-table">
                            <thead>
                              <tr>
                                <th>Lote ID</th>
                                <th>Tránsito</th>
                                <th>Rango de Placas</th>
                                <th>Cantidad Solic.</th>
                                <th>Consumidas</th>
                                <th>Fecha Entrega</th>
                                <th>Estado Cobro Lote</th>
                                <th>Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lotes.map(l => (
                                <tr key={l.id}>
                                  <td><strong>Lote #{l.id}</strong></td>
                                  <td>{l.transito_name || 'N/A'}</td>
                                  <td>
                                    <span className="plate-badge-sm">{l.start_plate || 'N/A'}</span> ➔ <span className="plate-badge-sm">{l.end_plate || 'N/A'}</span>
                                  </td>
                                  <td><strong>{l.requested_quantity}</strong></td>
                                  <td>
                                    <span style={{ fontWeight: 'bold', color: l.consumed_count > 0 ? '#3b82f6' : '#64748b' }}>
                                      {l.consumed_count} / {l.requested_quantity}
                                    </span>
                                  </td>
                                  <td>{formatDateShort(l.created_at)}</td>
                                  <td>
                                    <span className={`status-badge ${l.lote_estado_cobro === 'cobrado' ? 'status-active' : 'status-pending'}`}>
                                      {l.lote_estado_cobro.toUpperCase()}
                                    </span>
                                  </td>
                                  <td>
                                    {l.lote_estado_cobro === 'cobrado' ? (
                                      <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '12px' }}>
                                        ✓ Cobrado ({formatDateShort(l.lote_fecha_cobro)})
                                      </span>
                                    ) : (
                                      <button
                                        className="btn-primary"
                                        onClick={() => handleCobrarLoteBase(l.id)}
                                        style={{ background: '#3b82f6', padding: '6px 12px', fontSize: '12px' }}
                                      >
                                        🧾 Marcar Cobrado (Manual)
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sub-vista 2: Diferencial Prendas */}
                  {conceptTab === 'prendas' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <h4 style={{ margin: 0, color: '#1e293b' }}>🔒 Trámites con Inscripción / Levantamiento de Prenda</h4>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>Filtra los trámites consumidos que incluyeron prenda para cobrar el diferencial</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          {/* Filtro de estado de cobro de prenda */}
                          <select 
                            className="form-input" 
                            value={filterEstadoPrenda}
                            onChange={(e) => setFilterEstadoPrenda(e.target.value)}
                            style={{ minWidth: '180px', fontSize: '13px' }}
                          >
                            <option value="todos">📋 Todos los Estados</option>
                            <option value="pendiente">⏳ Pendientes</option>
                            <option value="cobrado">💵 Cobrados</option>
                            <option value="recaudado">✅ Recaudados</option>
                          </select>
                          <button
                            className="btn-secondary"
                            onClick={exportPrendasToExcel}
                            style={{ background: '#10b981', color: 'white', borderColor: '#059669', marginRight: '5px' }}
                          >
                            📥 Exportar Excel Prendas
                          </button>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>{selectedPrendaIds.length} seleccionados</span>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdatePrendaStatus('pendiente')}
                            disabled={selectedPrendaIds.length === 0}
                            style={{ background: '#f59e0b', borderColor: '#d97706' }}
                            title="Devolver a estado pendiente"
                          >
                            ⬅️ Devolver a Pendiente
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdatePrendaStatus('cobrado')}
                            disabled={selectedPrendaIds.length === 0}
                            style={{ background: '#8b5cf6' }}
                          >
                            🔒 1. Marcar Prenda Cobrado
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdatePrendaStatus('recaudado')}
                            disabled={selectedPrendaIds.length === 0}
                            style={{ background: '#059669' }}
                          >
                            💰 2. Marcar Prenda Recaudado
                          </button>
                        </div>
                      </div>
                      {loadingPrendas ? (
                        <p className="loading">Cargando trámites con prenda...</p>
                      ) : prendas.length === 0 ? (
                        <div className="no-data">No se encontraron trámites con inscripción de prenda para este concesionario.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="users-table">
                            <thead>
                              <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedPrendaIds(prendas.map(p => p.id));
                                      else setSelectedPrendaIds([]);
                                    }}
                                    checked={prendas.length > 0 && selectedPrendaIds.length === prendas.length}
                                  />
                                </th>
                                <th>ID Trámite</th>
                                <th>Placa</th>
                                <th>Propietario / Cliente</th>
                                <th>Punto de Venta</th>
                                <th>Tipo Trámite</th>
                                <th>Acreedor Prendario</th>
                                <th>Estado Cobro Prenda</th>
                              </tr>
                            </thead>
                            <tbody>
                              {prendas.map(p => {
                                const datos = p.datos_adicionales || {};
                                return (
                                  <tr key={p.id}>
                                    <td style={{ textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={selectedPrendaIds.includes(p.id)}
                                        onChange={() => {
                                          if (selectedPrendaIds.includes(p.id)) {
                                            setSelectedPrendaIds(selectedPrendaIds.filter(id => id !== p.id));
                                          } else {
                                            setSelectedPrendaIds([...selectedPrendaIds, p.id]);
                                          }
                                        }}
                                      />
                                    </td>
                                    <td><strong>#{p.id}</strong></td>
                                    <td><span className="plate-badge-sm">{p.plate || 'Sin Placa'}</span></td>
                                    <td>
                                      <strong>{datos.propietario || 'N/A'}</strong>
                                      <small style={{ display: 'block', color: '#64748b' }}>{datos.tipo_documento || 'CC'}: {datos.documento || 'N/A'}</small>
                                    </td>
                                    <td>{p.punto_venta_name || 'N/A'}</td>
                                    <td>{p.tipo_tramite}</td>
                                    <td>{datos.acreedor_nombre || 'N/A'}</td>
                                    <td>
                                      <span className={`status-badge ${p.prenda_estado_cobro === 'recaudado' ? 'status-active' : p.prenda_estado_cobro === 'cobrado' ? 'status-pending' : 'status-inactive'}`}>
                                        {p.prenda_estado_cobro.toUpperCase()}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sub-vista 3: Impuestos Vehiculares */}
                  {conceptTab === 'impuestos' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <h4 style={{ margin: 0, color: '#1e293b' }}>📄 Trámites con Pago de Impuesto Vehicular</h4>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>Filtra los trámites con cobro de impuesto para solicitar su reembolso</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          {/* Filtro de estado de cobro de impuesto */}
                          <select 
                            className="form-input" 
                            value={filterEstadoImpuesto}
                            onChange={(e) => setFilterEstadoImpuesto(e.target.value)}
                            style={{ minWidth: '180px', fontSize: '13px' }}
                          >
                            <option value="todos">📋 Todos los Estados</option>
                            <option value="pendiente">⏳ Pendientes</option>
                            <option value="cobrado">💵 Cobrados</option>
                            <option value="recaudado">✅ Recaudados</option>
                          </select>
                          <button
                            className="btn-secondary"
                            onClick={exportImpuestosToExcel}
                            style={{ background: '#10b981', color: 'white', borderColor: '#059669', marginRight: '5px' }}
                          >
                            📥 Exportar Excel Impuestos
                          </button>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>{selectedImpuestoIds.length} seleccionados</span>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdateImpuestoStatus('pendiente')}
                            disabled={selectedImpuestoIds.length === 0}
                            style={{ background: '#f59e0b', borderColor: '#d97706' }}
                            title="Devolver a estado pendiente"
                          >
                            ⬅️ Devolver a Pendiente
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdateImpuestoStatus('cobrado')}
                            disabled={selectedImpuestoIds.length === 0}
                            style={{ background: '#3b82f6' }}
                          >
                            🧾 1. Marcar Impuesto Cobrado
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdateImpuestoStatus('recaudado')}
                            disabled={selectedImpuestoIds.length === 0}
                            style={{ background: '#059669' }}
                          >
                            💰 2. Marcar Impuesto Recaudado
                          </button>
                        </div>
                      </div>
                      {loadingImpuestos ? (
                        <p className="loading">Cargando trámites con impuesto...</p>
                      ) : impuestos.length === 0 ? (
                        <div className="no-data">No se encontraron trámites con cobro de impuesto vehicular para este concesionario.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="users-table">
                            <thead>
                              <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedImpuestoIds(impuestos.map(i => i.id));
                                      else setSelectedImpuestoIds([]);
                                    }}
                                    checked={impuestos.length > 0 && selectedImpuestoIds.length === impuestos.length}
                                  />
                                </th>
                                <th>ID Trámite</th>
                                <th>Placa</th>
                                <th>Propietario / Cliente</th>
                                <th>Punto de Venta</th>
                                <th>Valor Impuesto</th>
                                <th>Estado Cobro Impuesto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {impuestos.map(imp => {
                                const datos = imp.datos_adicionales || {};
                                return (
                                  <tr key={imp.id}>
                                    <td style={{ textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={selectedImpuestoIds.includes(imp.id)}
                                        onChange={() => {
                                          if (selectedImpuestoIds.includes(imp.id)) {
                                            setSelectedImpuestoIds(selectedImpuestoIds.filter(id => id !== imp.id));
                                          } else {
                                            setSelectedImpuestoIds([...selectedImpuestoIds, imp.id]);
                                          }
                                        }}
                                      />
                                    </td>
                                    <td><strong>#{imp.id}</strong></td>
                                    <td><span className="plate-badge-sm">{imp.plate || 'Sin Placa'}</span></td>
                                    <td>
                                      <strong>{datos.propietario || 'N/A'}</strong>
                                      <small style={{ display: 'block', color: '#64748b' }}>{datos.tipo_documento || 'CC'}: {datos.documento || 'N/A'}</small>
                                    </td>
                                    <td>{imp.punto_venta_name || 'N/A'}</td>
                                    <td>
                                      <strong style={{ color: '#059669', fontSize: '14px' }}>
                                        ${parseFloat(imp.impuesto_valor || 0).toLocaleString('es-CO')}
                                      </strong>
                                    </td>
                                    <td>
                                      <span className={`status-badge ${imp.impuesto_estado_cobro === 'recaudado' ? 'status-active' : imp.impuesto_estado_cobro === 'cobrado' ? 'status-pending' : 'status-inactive'}`}>
                                        {imp.impuesto_estado_cobro.toUpperCase()}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <HistorialLotesModal
        isOpen={showHistorialModal}
        onClose={() => setShowHistorialModal(false)}
        concesionarioId={selectedConcesionario?.concesionario_id}
        concesionarioName={selectedConcesionario?.concesionario_name}
        onRefreshData={() => {
          fetchStats();
          if (selectedConcesionario) {
            loadTramites(selectedConcesionario.concesionario_id);
          }
        }}
      />
    </div>
  );
};

export default Liquidacion;
